import React, { useContext } from 'react';
import {
  AppBar,
  Drawer,
  Toolbar,
  List,
  CssBaseline,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Select,
  Menu,
  MenuItem
} from '@mui/material';
import { withStyles } from '@mui/styles';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon,
  ExitToApp as ExitToAppIcon
} from '@mui/icons-material';
import classNames from 'classnames';

import encodedNavLogo from '../encodednavlogo.js';
import styles from './styles.js';
import FullPageSpinner from './fullPageSpinner.jsx';
import { NavigationItem } from './navigationItem.jsx';
import { useLayout } from './useLayout.js';

function UnstyledLayout(props) {
  const { classes } = props;

  const [layoutState, layoutApi] = useLayout(props.getClientRoutes, props.backendRoutes);

  const isAppBarMenuOpen = Boolean(layoutState.appBarMenuAnchor);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={classNames(classes.appBar, {
          [classes.appBarShift]: layoutState.isDrawerOpen
        })}

      >
        <Toolbar
          className={classNames(classes.appBar, {
            [classes.appBarShift]: layoutState.isDrawerOpen
          })}
        >
          <IconButton
            disabled={layoutState.hideNav}
            color="inherit"
            aria-label="Open drawer"
            onClick={layoutApi.handleDrawerOpen}
            className={classNames(classes.menuButton, {
              [classes.hide]: layoutState.isDrawerOpen
            })}
          >
            <MenuIcon />
          </IconButton>

          {/* base64 encoded image of medkoder logo START */}
          <img
            style={{ height: '30px' }}
            src={`data:image/png;base64,${encodedNavLogo}`}
            alt="MedKoder"
          />
          {/* base64 encoded image of medkoder logo END */}

          {/* organization select START */}
          <Select
            disabled={layoutState.hideNav}
            open={layoutState.isOrganizationSelectOpen}
            onOpen={layoutApi.handleOrganizationSelectOpen}
            onClose={layoutApi.handleOrganizationSelectClose}
            onChange={layoutApi.handleOrganizationSelectChange}
            value={layoutState.sessionInfo.currentOrganizationId}
            inputProps={{
              name: 'organizationSelect',
              id: 'organization-select'
            }}
            className={classes.organizationSelect}
          >
            {
              layoutState.sessionInfo.organizationList.map(org => {
                return (
                  <MenuItem key={org.organizationId} value={org.organizationId}>
                    {org.name}
                  </MenuItem>
                );
              })
            }
          </Select>
          {/* organization select END */}

          {/* menu START */}
          <IconButton
            disabled={layoutState.hideNav}
            onClick={layoutApi.handleAppBarMenuOpen}
            aria-owns={isAppBarMenuOpen ? 'app-bar-menu' : undefined}
            color="inherit"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="app-bar-menu"
            onClose={layoutApi.handleAppBarMenuClose}
            open={isAppBarMenuOpen}
            anchorEl={layoutState.appBarMenuAnchor}
          >
            <MenuItem onClick={layoutApi.handleChangePassword}>
              <ListItemIcon>
                <LockIcon />
              </ListItemIcon>
              <ListItemText inset primary="Change Password" />
            </MenuItem>
            <MenuItem onClick={layoutApi.handleLogout}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText inset primary="Logout" />
            </MenuItem>
          </Menu>
          {/* menu END */}
        </Toolbar>
      </AppBar>

      {
        layoutState.hideNav !== true
          ? (<Drawer
              anchor="left"
              variant="persistent"
              classes={{
                paper: classNames(
                  classes.drawerPaper,
                  !layoutState.isDrawerOpen && classes.drawerPaperClose,
                  layoutState.isDrawerOpen && classes.drawerPaperOpen
                )
              }}
              open={layoutState.isDrawerOpen}
            >
            <div className={classNames(classes.toolbar, classes.drawerBar)}>
              <IconButton
                onClick={layoutApi.handleDrawerClose}
                className={classes.blackColor}
              >
                {
                  props.theme.direction === 'rtl'
                    ? <ChevronRightIcon />
                    : <ChevronLeftIcon />
                }
              </IconButton>
            </div>

            <Divider />

            <List dense disablePadding>
              {
                layoutState.navigationItems.map(
                  (item, index) => (
                    <NavigationItem
                      key={item.id}
                      item={item}
                      index={index}
                      classes={classes}
                      selectedNavigationIndices={
                        layoutState.selectedNavigationIndices
                      }
                      handleToggleCollapsed={layoutApi.handleToggleCollapsed}
                      handleChangePage={layoutApi.handleChangePage}
                    />
                  )
                )
              }
            </List>
          </Drawer>)
        : null
      }
      <main className={classes.content}>
        <div className={classes.toolbar}></div>
        {layoutState.clientRoutes}
      </main>
      <FullPageSpinner isVisible={layoutState.isRequestInProgress} />
    </div>
  );
}

const LayoutWithStyles = withStyles(styles, { withTheme: true })(UnstyledLayout);

export { LayoutWithStyles as Layout };
